/**
 * StatementsByCustomer — vista scadenziario raggruppata per cliente.
 * Colonne: Scaduto / A scadere / Pagato / Aperto. Clic sulla riga → dettaglio
 * scadenze del cliente (espansione inline) con possibilità di registrare incassi.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, Banknote, Search, ArrowUpDown, FileDown, Mail, Share2 } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { API } from "../utils/format";
import { formatMoney } from "../utils/currency";
import { useTableSort, SortableTh } from "./tableSort";

const fmtDate = (iso) => iso ? new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("it-IT") : "—";

const STATUS_LABELS = { open: "Aperto", partial: "Parziale", paid: "Pagato", overdue: "Scaduto" };
const STATUS_COLORS = {
  open: "bg-blue-100 text-blue-800 border-blue-200",
  partial: "bg-yellow-100 text-yellow-800 border-yellow-200",
  paid: "bg-green-100 text-green-800 border-green-200",
  overdue: "bg-red-100 text-red-800 border-red-200",
};

const SORTERS = {
  scaduto_desc: (a, b) => b.scaduto - a.scaduto || b.aperto - a.aperto,
  aperto_desc: (a, b) => b.aperto - a.aperto,
  aperto_asc: (a, b) => a.aperto - b.aperto,
  name_asc: (a, b) => (a.business_name || "").localeCompare(b.business_name || "", "it"),
  name_desc: (a, b) => (b.business_name || "").localeCompare(a.business_name || "", "it"),
};

export default function StatementsByCustomer({ company, canRecordPayment, onPay, agentId, reloadKey, onProspetto, onSendProspetto, onShareProspetto }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState("scaduto_desc");
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const gSort = useTableSort();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (agentId) params.agent_id = agentId;
      const r = await axios.get(`${API}/sales-documents/by-customer`, { params });
      setGroups(r.data || []);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    load();
    setDetails({});
  }, [load, reloadKey]);

  // Carica (o ricarica dopo un incasso) il dettaglio del cliente espanso
  useEffect(() => {
    if (!expanded || details[expanded]) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/sales-documents`, { params: { customer_id: expanded } });
        if (!cancelled) setDetails((d) => ({ ...d, [expanded]: r.data || [] }));
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [expanded, details]);

  const toggle = (customerId) => setExpanded((cur) => (cur === customerId ? null : customerId));

  const filtered = gSort.sortList(
    groups
      .filter((g) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (g.business_name || "").toLowerCase().includes(q) || (g.codice_cliente || "").toLowerCase().includes(q);
      })
      .sort(SORTERS[sortMode] || SORTERS.scaduto_desc)
  );

  const totals = filtered.reduce(
    (acc, g) => ({
      scaduto: acc.scaduto + g.scaduto,
      a_scadere: acc.a_scadere + g.a_scadere,
      pagato: acc.pagato + g.pagato,
      aperto: acc.aperto + g.aperto,
    }),
    { scaduto: 0, a_scadere: 0, pagato: 0, aperto: 0 }
  );

  return (
    <Card className="p-0 overflow-hidden bg-white border-stone-200" data-testid="statements-by-customer">
      <div className="p-3 border-b border-stone-100 flex items-center gap-2">
        <Search className="w-4 h-4 text-stone-400" />
        <Input
          placeholder="Cerca cliente per nome o codice..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs h-8"
          data-testid="customer-group-search"
        />
        <div className="flex items-center gap-1.5">
          <ArrowUpDown className="w-3.5 h-3.5 text-stone-400" />
          <Select value={sortMode} onValueChange={setSortMode}>
            <SelectTrigger className="w-48 h-8 text-xs bg-white border-stone-300" data-testid="statements-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="scaduto_desc">Scaduto: prima i maggiori</SelectItem>
              <SelectItem value="aperto_desc">Aperto: prima i maggiori</SelectItem>
              <SelectItem value="aperto_asc">Aperto: prima i minori</SelectItem>
              <SelectItem value="name_asc">Cliente A → Z</SelectItem>
              <SelectItem value="name_desc">Cliente Z → A</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <span className="ml-auto text-xs text-stone-500">{filtered.length} clienti</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-100 text-left text-xs uppercase text-stone-600">
            <tr>
              <th className="p-3 w-8"></th>
              <SortableTh label="Cliente" sortId="business_name" sort={gSort} />
              <SortableTh label="Agente" sortId="agent_name" sort={gSort} />
              <SortableTh label="Scadenze" sortId="docs_count" sort={gSort} className="text-center" />
              <SortableTh label="Scaduto" sortId="scaduto" sort={gSort} align="right" />
              <SortableTh label="A scadere" sortId="a_scadere" sort={gSort} align="right" />
              <SortableTh label="Pagato" sortId="pagato" sort={gSort} align="right" />
              <SortableTh label="Aperto tot." sortId="aperto" sort={gSort} align="right" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-stone-500">Caricamento...</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-stone-500 italic">Nessun cliente con scadenze</td></tr>
            )}
            {!loading && filtered.map((g) => (
              <React.Fragment key={g.customer_id}>
                <tr
                  className={`border-t border-stone-100 cursor-pointer hover:bg-stone-50 ${expanded === g.customer_id ? "bg-stone-50" : ""}`}
                  onClick={() => toggle(g.customer_id)}
                  data-testid={`customer-group-row-${g.codice_cliente || g.customer_id}`}
                >
                  <td className="p-3 text-stone-400">
                    {expanded === g.customer_id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </td>
                  <td className="p-3">
                    <span className="font-semibold text-stone-900">{g.business_name || "—"}</span>
                    {g.codice_cliente && <span className="ml-2 font-mono text-[10px] text-stone-400">{g.codice_cliente}</span>}
                  </td>
                  <td className="p-3 text-stone-600 text-xs">{g.agent_name || "—"}</td>
                  <td className="p-3 text-center text-stone-600">
                    {g.docs_count}
                    {g.overdue_count > 0 && <Badge className="ml-1.5 bg-red-100 text-red-800 border border-red-200 text-[10px] py-0">{g.overdue_count} scad.</Badge>}
                  </td>
                  <td className={`p-3 text-right font-semibold ${g.scaduto > 0 ? "text-red-700" : "text-stone-400"}`}>
                    {g.scaduto !== 0 ? formatMoney(g.scaduto, company) : "—"}
                  </td>
                  <td className={`p-3 text-right ${g.a_scadere !== 0 ? "text-blue-700" : "text-stone-400"}`}>
                    {g.a_scadere !== 0 ? formatMoney(g.a_scadere, company) : "—"}
                  </td>
                  <td className="p-3 text-right text-green-700">{formatMoney(g.pagato, company)}</td>
                  <td className="p-3 text-right font-bold text-stone-900">{formatMoney(g.aperto, company)}</td>
                </tr>
                {expanded === g.customer_id && (
                  <tr className="bg-stone-50/60" data-testid={`customer-group-detail-${g.codice_cliente || g.customer_id}`}>
                    <td colSpan={8} className="p-0">
                      {(onProspetto || onSendProspetto) && (
                        <div className="px-6 pt-3 flex flex-wrap gap-2">
                          {onProspetto && (
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onProspetto(g); }} className="h-7 text-xs border-stone-300" data-testid={`prospetto-pdf-${g.codice_cliente || g.customer_id}`}>
                              <FileDown className="w-3.5 h-3.5 mr-1" /> Prospetto PDF
                            </Button>
                          )}
                          {onShareProspetto && (
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onShareProspetto(g); }} className="h-7 text-xs border-stone-300" data-testid={`prospetto-share-${g.codice_cliente || g.customer_id}`}>
                              <Share2 className="w-3.5 h-3.5 mr-1" /> Condividi
                            </Button>
                          )}
                          {onSendProspetto && (
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onSendProspetto(g); }} className="h-7 text-xs border-[#8A1C2A]/40 text-[#8A1C2A] hover:bg-[#8A1C2A]/5" data-testid={`prospetto-mail-${g.codice_cliente || g.customer_id}`}>
                              <Mail className="w-3.5 h-3.5 mr-1" /> Invia prospetto al cliente
                            </Button>
                          )}
                        </div>
                      )}
                      <CustomerDetailTable
                        docs={details[g.customer_id]}
                        company={company}
                        canRecordPayment={canRecordPayment}
                        onPay={onPay}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          {!loading && filtered.length > 0 && (
            <tfoot className="bg-stone-100 border-t-2 border-stone-200 text-sm font-bold">
              <tr>
                <td className="p-3" colSpan={4}>TOTALE</td>
                <td className="p-3 text-right text-red-700">{formatMoney(totals.scaduto, company)}</td>
                <td className="p-3 text-right text-blue-700">{formatMoney(totals.a_scadere, company)}</td>
                <td className="p-3 text-right text-green-700">{formatMoney(totals.pagato, company)}</td>
                <td className="p-3 text-right">{formatMoney(totals.aperto, company)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
}

export function PaymentDatesCell({ doc, testId }) {
  const [open, setOpen] = useState(false);
  const pays = doc.payment_dates || [];
  if (pays.length === 0) return <span className="text-stone-300" data-testid={testId}>—</span>;
  const last = pays[pays.length - 1];
  const title = pays.map((p) => `${fmtDate(p.date)} · ${formatMoney(p.amount || 0, null)}`).join("\n");
  return (
    <div className="text-green-800" data-testid={testId} title={title}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (pays.length > 1) setOpen((v) => !v); }}
        className={`inline-flex items-center gap-1 ${pays.length > 1 ? "cursor-pointer underline decoration-dotted" : "cursor-default"}`}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        {fmtDate(last.date)}
        {pays.length > 1 && <span className="text-[9px] bg-green-100 border border-green-200 rounded px-1 font-semibold">{pays.length} pag.</span>}
      </button>
      {open && (
        <ul className="mt-0.5 space-y-0.5 text-[10px] text-stone-600" data-testid={testId ? `${testId}-list` : undefined}>
          {pays.map((p, i) => (
            <li key={i} className="whitespace-nowrap">{fmtDate(p.date)} · <span className="text-green-700">{formatMoney(p.amount || 0, null)}</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CustomerDetailTable({ docs, company, canRecordPayment, onPay }) {
  const dSort = useTableSort();
  if (!docs) return <div className="p-4 text-center text-xs text-stone-500">Caricamento dettaglio...</div>;
  if (docs.length === 0) return <div className="p-4 text-center text-xs text-stone-500 italic">Nessun documento</div>;
  const sorted = dSort.sortList(
    [...docs].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "") || (a.doc_date || "").localeCompare(b.doc_date || "")),
    { status: (d) => (d.is_overdue ? "overdue" : d.status), last_payment_date: (d) => d.last_payment_date || "" }
  );
  const totals = sorted.reduce(
    (acc, d) => ({ importo: acc.importo + (d.amount_total || 0), incassato: acc.incassato + (d.amount_paid || 0), aperto: acc.aperto + (d.amount_open || 0) }),
    { importo: 0, incassato: 0, aperto: 0 }
  );
  let progressivo = 0;
  const colSpan = canRecordPayment ? 10 : 9;
  return (
    <div className="px-6 py-3">
      <table className="w-full text-xs">
        <thead className="text-left text-[10px] uppercase text-stone-500">
          <tr>
            <SortableTh label="Numero" sortId="doc_number" sort={dSort} className="!p-1.5" />
            <SortableTh label="Data doc." sortId="doc_date" sort={dSort} className="!p-1.5" />
            <SortableTh label="Scadenza" sortId="due_date" sort={dSort} className="!p-1.5" />
            <SortableTh label="Importo" sortId="amount_total" sort={dSort} align="right" className="!p-1.5" />
            <SortableTh label="Incassato" sortId="amount_paid" sort={dSort} align="right" className="!p-1.5" />
            <SortableTh label="Pagato il" sortId="last_payment_date" sort={dSort} className="!p-1.5" />
            <SortableTh label="Aperto" sortId="amount_open" sort={dSort} align="right" className="!p-1.5" />
            <th className="p-1.5 text-right" title="Somma cumulata degli importi aperti (scaduto + a scadere) nell'ordine visualizzato, dal più vecchio al più recente">Progressivo aperto</th>
            <SortableTh label="Stato" sortId="status" sort={dSort} className="!p-1.5" />
            {canRecordPayment && <th className="p-1.5 text-right"></th>}
          </tr>
        </thead>
        <tbody>
          {sorted.map((d) => {
            const statusKey = d.is_overdue ? "overdue" : d.status;
            const hasOpen = Math.abs(d.amount_open || 0) > 0.004;
            if (hasOpen) progressivo += d.amount_open || 0;
            return (
              <tr key={d.id} className="border-t border-stone-200/60" data-testid={`detail-row-${d.doc_number}`}>
                <td className="p-1.5 font-mono text-stone-800">
                  {d.doc_number}
                  {d.amount_total < 0 && <Badge className="ml-1 bg-purple-100 text-purple-800 border border-purple-200 text-[9px] py-0">NC</Badge>}
                  {d.rata_tot > 1 && <span className="ml-1 text-[9px] text-stone-400">rata {d.rata_num}/{d.rata_tot}</span>}
                </td>
                <td className="p-1.5 text-stone-600">{fmtDate(d.doc_date)}</td>
                <td className={`p-1.5 ${d.is_overdue ? "text-red-700 font-semibold" : "text-stone-600"}`}>{fmtDate(d.due_date)}</td>
                <td className="p-1.5 text-right">{formatMoney(d.amount_total, company)}</td>
                <td className="p-1.5 text-right text-green-700">{formatMoney(d.amount_paid, company)}</td>
                <td className="p-1.5"><PaymentDatesCell doc={d} testId={`detail-paid-on-${d.doc_number}`} /></td>
                <td className="p-1.5 text-right font-semibold">{formatMoney(d.amount_open, company)}</td>
                <td className={`p-1.5 text-right ${hasOpen ? (d.is_overdue ? "text-red-700" : "text-stone-700") : "text-stone-300"}`} data-testid={`detail-progressivo-${d.doc_number}`}>
                  {hasOpen ? formatMoney(progressivo, company) : "—"}
                </td>
                <td className="p-1.5">
                  <Badge className={`${STATUS_COLORS[statusKey]} border text-[10px] py-0`}>{STATUS_LABELS[statusKey]}</Badge>
                </td>
                {canRecordPayment && (
                  <td className="p-1.5 text-right">
                    {d.status !== "paid" && d.amount_open > 0 && (
                      <Button
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onPay?.(d); }}
                        className="bg-[#8A1C2A] text-white h-6 text-[10px] px-2"
                        data-testid={`group-record-payment-${d.doc_number}`}
                      >
                        <Banknote className="w-3 h-3 mr-1" /> Incassa
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot className="border-t-2 border-stone-300 font-bold text-stone-800">
          <tr data-testid="detail-totals-row">
            <td className="p-1.5" colSpan={3}>TOTALE ({sorted.length} scadenze)</td>
            <td className="p-1.5 text-right">{formatMoney(totals.importo, company)}</td>
            <td className="p-1.5 text-right text-green-700">{formatMoney(totals.incassato, company)}</td>
            <td className="p-1.5"></td>
            <td className="p-1.5 text-right">{formatMoney(totals.aperto, company)}</td>
            <td className="p-1.5" colSpan={colSpan - 7}></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
